/**
 * PORTED — RO-3-03 / #44296.
 * Origin: `excellent/app/work/_dashboard.tsx`.
 *
 * Changes:
 *   resolveTenant / getDb / buildGoalsHome / listAllScopedTasksByStatus /
 *   loadProjectsBoard → `useApi(modes.workDashboard)` (one READ, same projection).
 *   next/link → RouterLink from the router seam.
 *
 * The body composition (four ListCards: Goals · Due · Projects · Roadmap) is
 * otherwise unchanged — a transport change, not a redesign.
 */

import {
  ListCard,
  ScopedTaskLifecycleList,
  GoalLedgerRow,
  HealthBar,
  objectHref,
  type GoalLedgerItem,
  type ProjectsBoardGroup,
} from "@excellent/ui";
import { Milestone } from "@excellent/ui/icons";
import { Link } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { modes } from "@excellent/ui/api/contracts/modes";
import type { ScopedTask } from "@/projects/types";
import { PERIOD_PHRASE, type WorkPeriod } from "./period";

export function WorkDashboard({
  period,
  projectSlug,
}: {
  period: WorkPeriod;
  projectSlug?: string;
}) {
  const { data, isLoading, error } = useApi(modes.workDashboard, {
    query: {
      period,
      ...(projectSlug ? { projectSlug } : {}),
    },
  });

  if (error) {
    return (
      <p className="px-6 type-body text-destructive">
        {error.message ?? "Failed to load the Work dashboard."}
      </p>
    );
  }
  if (isLoading || !data) {
    return <p className="px-6 type-body text-muted-foreground">Loading…</p>;
  }

  const topGoals = data.goals as GoalLedgerItem[];
  const dueRows = data.dueRows as ScopedTask[];
  const projects = data.projects as ProjectsBoardGroup[];
  const roadmap = data.roadmap;
  const base = data.base;
  const periodKey = (data.period as WorkPeriod) ?? period;

  return (
    <div className="flex flex-col gap-3">
      <ListCard
        title="Goals"
        count={topGoals.length}
        viewAllHref={`${base}/goals`}
        viewAllLabel="All goals"
        empty={
          <>
            No strategic goals yet.{" "}
            <Link href={`${base}/goals`} variant="inline">
              Create a goal →
            </Link>
          </>
        }
      >
        <div className="flex flex-col">
          {topGoals.map((g) => (
            <GoalLedgerRow
              key={g.id}
              item={g}
              href={g.uid ? objectHref(g.uid, "work") : `${base}/goals`}
            />
          ))}
        </div>
      </ListCard>

      <ListCard
        title="Due"
        count={dueRows.length}
        subtitle={periodKey === "today" ? undefined : PERIOD_PHRASE[periodKey]}
        viewAllHref={`${base}/issues`}
        viewAllLabel="All issues"
      >
        <ScopedTaskLifecycleList
          tasks={dueRows}
          gutter
          empty={`Nothing due ${PERIOD_PHRASE[periodKey]} — you're clear.`}
        />
      </ListCard>

      <ListCard
        title="Projects"
        count={projects.length}
        viewAllHref={`${base}/projects`}
        viewAllLabel="All projects"
        empty="No projects yet."
      >
        <div className="flex flex-col">
          {projects.map((p) => (
            <Link
              key={p.slug}
              variant="unstyled"
              href={`/projects/${p.slug}`}
              className="flex items-center gap-3 border-b border-border px-4 py-2.5 type-body transition-colors last:border-b-0 hover:bg-secondary"
            >
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: p.color ?? "var(--muted-foreground)" }}
              />
              <span className="min-w-0 flex-1 truncate text-foreground">{p.name}</span>
              <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
                {p.openCount} open
                {p.reviewCount > 0 ? ` · ${p.reviewCount} in review` : ""}
              </span>
            </Link>
          ))}
        </div>
      </ListCard>

      <ListCard
        title="Roadmap"
        count={roadmap.length}
        viewAllHref={
          projectSlug
            ? `/projects/${projectSlug}/work/goals`
            : "/work/goals"
        }
        viewAllLabel="Full roadmap"
        empty="No initiatives yet — goals you create become initiatives here."
      >
        <div className="flex flex-col">
          {roadmap.map((r) => (
            <Link
              key={r.uid}
              variant="unstyled"
              href={objectHref(r.uid, "work")}
              className="flex items-center gap-3 border-b border-border px-4 py-2.5 type-body transition-colors last:border-b-0 hover:bg-secondary"
            >
              <span
                aria-hidden
                className="inline-flex size-5 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary"
              >
                <Milestone className="size-3" />
              </span>
              <span className="min-w-0 flex-1 truncate text-foreground">{r.title}</span>
              <span className="hidden w-44 shrink-0 items-center justify-end gap-2 md:flex">
                <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">
                  {r.done_count}/{r.task_count}
                </span>
                <span className="w-24 shrink-0">
                  <HealthBar
                    value={r.done_count}
                    max={Math.max(r.task_count, 1)}
                    intent="success"
                  />
                </span>
              </span>
            </Link>
          ))}
        </div>
      </ListCard>
    </div>
  );
}
